import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { DxDrawerComponent, DxDrawerModule, DxDrawerTypes } from 'devextreme-angular/ui/drawer';
import { DxScrollViewComponent, DxScrollViewModule } from 'devextreme-angular/ui/scroll-view';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { injectNavigationEnd } from 'ngxtension/navigation-end';
import { map } from 'rxjs';
import { injectAppNavigation } from '../../app-navigation';
import { Screen } from '../../screen/screen';
import Header from '../header/header';
import SideNavigationMenu from '../side-navigation-menu/side-navigation-menu';

@Component({
  selector: 'app-side-nav-outer-toolbar',
  imports: [DxDrawerModule, Header, DxScrollViewModule, SideNavigationMenu, RouterOutlet],
  templateUrl: './side-nav-outer-toolbar.html',
  styleUrl: './side-nav-outer-toolbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class SideNavOuterToolbar {
  private readonly router = inject(Router);
  private readonly screen = inject(Screen);
  private readonly navigationEnd$ = injectNavigationEnd();
  private readonly hasNavItems = computed(() => this.appNavigation().length > 0);

  protected readonly appNavigation = injectAppNavigation();

  protected readonly scrollView = viewChild.required(DxScrollViewComponent);
  protected readonly drawers = viewChildren(DxDrawerComponent);

  protected readonly selectedRoute = toSignal(
    this.navigationEnd$.pipe(map((val) => val.urlAfterRedirects.split('?')[0])),
  );

  protected readonly menuOpened = model(
    this.screen.sizes()['screen-large'] && this.appNavigation().length > 0,
  );

  appTitle = input.required<string>();

  protected readonly menuMode = computed<DxDrawerTypes.OpenedStateMode>(() =>
    this.screen.sizes()['screen-large'] ? 'shrink' : 'overlap',
  );
  protected readonly menuRevealMode = computed<DxDrawerTypes.RevealMode>(() =>
    this.screen.isMobile() ? 'slide' : 'expand',
  );
  protected readonly minMenuSize = computed(() =>
    this.screen.isMobile() || this.appNavigation().length <= 0 ? 0 : 60,
  );
  protected readonly shaderEnabled = computed(() => this.minMenuSize() === 0);

  private readonly currentFullUrl = toSignal(
    this.navigationEnd$.pipe(map((val) => val.urlAfterRedirects)),
    { initialValue: this.router.url },
  );

  protected readonly isDetailOutletActive = computed(
    () => !!this.router.parseUrl(this.currentFullUrl()).root.children['detail'],
  );
  protected readonly detailPanelWidth = computed(() =>
    this.screen.isMobile()
      ? this.screen.windowWidth()
      : Math.round(this.screen.windowWidth() * 0.4),
  );

  constructor() {
    effect(() => {
      const hasNavItems = this.hasNavItems();
      if (!this.screen.sizes()['screen-large']) return;
      this.menuOpened.set(hasNavItems);
    });

    // DX drawers cache their content-pane width and dx-scroll-view caches its
    // scrollable-content width against that pane. Neither reliably recomputes
    // on DevTools device-emulation toggles. Repaint drawers outer-to-inner,
    // then update the scroll-view last, all in one effect body — splitting
    // these into separate effects (via ResponsiveRepaint directive) was
    // observed to break custom scrollbar styling on nested dx-lists.
    afterRenderEffect(() => {
      this.screen.windowWidth();
      this.drawers().forEach((drawer) => drawer.instance.repaint());
      this.scrollView().instance.update();
    });
  }

  protected navigationChanged(event: DxTreeViewTypes.ItemClickEvent) {
    if (!event.itemData) return;

    const path = event.itemData['path'];
    const pointerEvent = event.event;

    if (!path) {
      if (!this.menuOpened()) this.menuOpened.set(true);
      return;
    }

    if (event.node?.selected) {
      pointerEvent?.preventDefault();
      return;
    }

    this.router.navigate([path]);
    this.scrollView().instance.scrollTo(0);

    // On true mobile (drawer fully hidden when closed), close after navigation
    if (this.minMenuSize() === 0) {
      this.menuOpened.set(false);
      pointerEvent?.stopPropagation();
    }
  }

  protected onDetailPanelClosed(opened: boolean): void {
    if (!opened && this.isDetailOutletActive()) {
      this.router.navigate([{ outlets: { detail: null } }], { queryParamsHandling: 'preserve' });
    }
  }
}
